import type { PrayerSettings, PrayerTimings } from "@/data/types";
import { addIstDays, parseHHmmInIst, toIst } from "@/lib/time";

import type { DaySchedule, PrayerName, PrayerSlot } from "./types";

/**
 * Build a DaySchedule from the masjid's committee-set timings.
 *
 * This module deliberately does NO astronomical calculation, NO fallback to a
 * Karachi/MWL method, and NO derivation of Jama'ah from Azaan. The values you
 * see here are exactly what the admin saved to `settings/prayer` in Firestore.
 *
 * If a stored "HH:mm" is malformed or empty, the slot falls back to 00:00 IST
 * for that day so the UI still renders a valid Date (the visibly-wrong 12:00 AM
 * value is intentional — it signals the admin that a value needs fixing).
 */

const PRAYER_ORDER: PrayerName[] = ["fajr", "dhuhr", "asr", "maghrib", "isha"];

const BASE_LABELS: Record<PrayerName, string> = {
  fajr: "Fajr",
  dhuhr: "Dhuhr",
  asr: "Asr",
  maghrib: "Maghrib",
  isha: "Isha",
};

function fallbackMidnight(istDay: Date): Date {
  const parsed = parseHHmmInIst("00:00", istDay);
  return parsed ?? new Date(istDay.getTime());
}

function makeSlot(
  name: PrayerName,
  label: string,
  timings: PrayerTimings | undefined,
  istDay: Date,
): PrayerSlot {
  const fb = fallbackMidnight(istDay);
  const azaanTime =
    (timings ? parseHHmmInIst(timings.azaan, istDay) : null) ?? fb;
  const jamaahTime =
    (timings ? parseHHmmInIst(timings.jamaah, istDay) : null) ?? azaanTime;
  return { name, label, azaanTime, jamaahTime };
}

export function calculateScheduleForDate(
  referenceUtc: Date,
  settings: PrayerSettings,
): DaySchedule {
  const istDay = toIst(referenceUtc);
  const isFriday = istDay.getDay() === 5;

  const prayers: PrayerSlot[] = PRAYER_ORDER.map((name) =>
    makeSlot(
      name,
      name === "dhuhr" && isFriday ? "Jumu'ah" : BASE_LABELS[name],
      settings[name],
      istDay,
    ),
  );

  return { date: istDay, prayers, isFriday };
}

export function calculateTodaySchedule(
  settings: PrayerSettings,
  now: Date = new Date(),
): DaySchedule {
  return calculateScheduleForDate(now, settings);
}

export function calculateTomorrowSchedule(
  settings: PrayerSettings,
  now: Date = new Date(),
): DaySchedule {
  return calculateScheduleForDate(addIstDays(now, 1), settings);
}
